import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useAppApi } from "@/App";
import { useRequest } from "@components/AppHook";
import { DndTable } from "@components/DND";
import { DevForm, FormFieldGroup, FormFieldHelpText } from "@components/Forms/Fields";
import { Awesome, DevModal, Hint } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { CommonCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";

const controlType = {
    radio: "radio",
    checkbox: "checkbox",
    button: "button",
    field: "field",
};

const subControlType = {
    image_button: "image_button",
    video_button: "video_button",
    text_field: "text_field",
    number_field: "number_field",
    date_time_field: "date_time_field",
    choice_field: "choice_field",
    multi_choice_field: "multi_choice_field",
    url_field: "url_field",
};

const imageDownloadType = {
    exact: "exact",
    between: "between",
};

const allowedImageTypes = ["png", "jpeg"];
const allowedVideoTypes = ["webm"];

export default function SchemeItemListScreen() {
    const { t } = useTranslation();
    const { apiData, setApiData } = useAppApi(url_reverse("api:schema_item:list"));
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [updatedObject, setUpdatedObject] = useState({});

    const handleOpenEditModal = (object) => {
        setUpdatedObject(object);
        setShowEditModal(true);
    };
    const handleCloseEditModal = () => {
        setShowEditModal(false);
    };

    const header = (
        <>
            <Link to={url_reverse("admin:event:list")}>{t("Настройки")}</Link>
            <span> – </span>
            {t("Управление полями")}
        </>
    );
    const headerTools = (
        <>
            <Button onClick={() => setShowModal(true)} className="btn btn-sm btn-dark">
                <Awesome type="plus-circle" /> {t("Создать поле")}
            </Button>
        </>
    );
    return (
        <DefaultLayout>
            <CommonCard header={header} headerTools={headerTools}>
                <SchemeItemListSortable
                    apiData={apiData}
                    setApiData={setApiData}
                    handleOpenEditModal={handleOpenEditModal}
                />
                <CreateSchemeItemModal showModal={showModal} setShowModal={setShowModal} setApiData={setApiData} />
                <UpdateSchemeItemModal
                    showEditModal={showEditModal}
                    handleCloseEditModal={handleCloseEditModal}
                    setApiData={setApiData}
                    updatedObject={updatedObject}
                    setUpdatedObject={setUpdatedObject}
                />
            </CommonCard>
        </DefaultLayout>
    );
}

function UpdateSchemeItemModal({ showEditModal, handleCloseEditModal, setApiData, updatedObject, setUpdatedObject }) {
    const { t } = useTranslation();
    const [errors, setErrors] = useState({});
    const { api_post } = useRequest();

    const control_type_labels = {
        button: t("Кнопка"),
        radio: t("Радиобаттон"),
        checkbox: t("Чекбокс"),
        field: t("Поле"),
    };

    const subControlTypeLabels = {
        image_button: t("Изображения"),
        video_button: t("Видео"),
    };

    const fieldTypeChoices = [
        { value: subControlType.text_field, label: t("Текстовое поле") },
        { value: subControlType.number_field, label: t("Числовое поле") },
        { value: subControlType.date_time_field, label: t("Даты и времени") },
        { value: subControlType.choice_field, label: t("Поле выбора") },
        { value: subControlType.url_field, label: t("Линк") },
        { value: subControlType.multi_choice_field, label: t("Множественный выбор") },
    ];

    const checkTypeChoices = [
        { value: "exact", label: t("Конкретный размер") },
        { value: "between", label: t("В пределах") },
    ];

    const subControlTypeChoices = [
        { value: subControlType.image_button, label: t("Изображения") },
        { value: subControlType.video_button, label: t("Видео") },
    ];

    const submitUpdateEventItem = (ev) => {
        const newConfiguration = {
            ...updatedObject,
            button_parameters: JSON.stringify(updatedObject.button_parameters),
            radio_parameters: JSON.stringify(updatedObject.radio_parameters),
            checkbox_parameters: JSON.stringify(updatedObject.checkbox_parameters),
            field_parameters: JSON.stringify(updatedObject.field_parameters),
        };
        api_post(ev, url_reverse("api:schema_item:list"), {
            params: { action: "update_event_item", ...newConfiguration },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                    setErrors({});
                    handleCloseEditModal();
                } else if (resp.errors) {
                    setErrors(resp.errors);
                }
            },
        });
    };
    const handleUpdateEventItem = (e) => {
        const { value, name } = e.target;
        setUpdatedObject((prevState) => {
            if (
                name === "check" ||
                name === "x" ||
                name === "y" ||
                name === "x_2" ||
                name === "y_2" ||
                name === "max_volume" ||
                name === "file_format"
            ) {
                if (name === "file_format") {
                    const currentFormat = updatedObject.button_parameters.file_format || [];
                    const updatedButtonParameters = e.target.checked
                        ? [...currentFormat, value]
                        : updatedObject.button_parameters.file_format.filter((item) => item !== value);

                    return {
                        ...prevState,
                        button_parameters: {
                            ...prevState.button_parameters,
                            [name]: updatedButtonParameters,
                        },
                    };
                }
                const updatedButtonParameters = { ...prevState.button_parameters, [name]: value };
                return {
                    ...prevState,
                    button_parameters: updatedButtonParameters,
                };
            }
            if (name === "is_developer_fill_allowed") {
                return {
                    ...prevState,
                    [name]: e.target.checked,
                };
            }
            if (
                name === "radio" ||
                name === "radio_note" ||
                name === "radio_initial" ||
                name === "radio_en" ||
                name === "radio_note_en"
            ) {
                if (name === "radio_initial") {
                    const updatedRadioParameters = prevState.radio_parameters.map((item) => ({
                        ...item,
                        radio_initial: item.id === parseInt(e.target.dataset.id),
                    }));
                    return {
                        ...prevState,
                        radio_parameters: updatedRadioParameters,
                    };
                }

                const updatedRadioButtons = prevState.radio_parameters.map((radioButtonField) => {
                    if (radioButtonField.id === parseInt(e.target.dataset.id)) {
                        return { ...radioButtonField, [name]: value };
                    }
                    return radioButtonField;
                });

                return {
                    ...prevState,
                    radio_parameters: updatedRadioButtons,
                };
            }
            if (
                name === "checkbox" ||
                name === "checkbox_note" ||
                name === "checkbox_en" ||
                name === "checkbox_note_en"
            ) {
                const updatedCheckboxButtons = prevState.checkbox_parameters.map((checkboxButtonField) => {
                    if (checkboxButtonField.id === parseInt(e.target.dataset.id)) {
                        return { ...checkboxButtonField, [name]: value };
                    }
                    return checkboxButtonField;
                });

                return {
                    ...prevState,
                    checkbox_parameters: updatedCheckboxButtons,
                };
            }
            if (name === "max_length" || name === "max_num" || name === "field_choice" || name === "field_choice_en") {
                if (name === "field_choice") {
                    const updatedFieldChoices = prevState.field_parameters.choices.map((fieldChoice) => {
                        if (fieldChoice.id === parseInt(e.target.dataset.id)) {
                            return { ...fieldChoice, label: value };
                        }
                        return fieldChoice;
                    });
                    return {
                        ...prevState,
                        field_parameters: {
                            ...prevState.field_parameters,
                            choices: updatedFieldChoices,
                        },
                    };
                }
                if (name === "field_choice_en") {
                    const updatedFieldChoices = prevState.field_parameters.choices.map((fieldChoice) => {
                        if (fieldChoice.id === parseInt(e.target.dataset.id)) {
                            return { ...fieldChoice, label_en: value };
                        }
                        return fieldChoice;
                    });
                    return {
                        ...prevState,
                        field_parameters: {
                            ...prevState.field_parameters,
                            choices: updatedFieldChoices,
                        },
                    };
                }
                const updatedFieldParameters = { ...prevState.field_parameters, [name]: value };
                return {
                    ...prevState,
                    field_parameters: updatedFieldParameters,
                };
            }
            if (value === subControlType.video_button) {
                const updatedButtonParameters = {
                    ...prevState.button_parameters,
                    check: imageDownloadType.exact,
                    file_format: allowedVideoTypes,
                };
                return {
                    ...prevState,
                    button_parameters: updatedButtonParameters,
                    [name]: value,
                };
            }
            return {
                ...prevState,
                [name]: value,
            };
        });
    };

    const addAdditionalRadioField = (ev) => {
        ev.preventDefault();
        const newFieldId = updatedObject.radio_parameters.length + 1;
        setUpdatedObject((prevConfig) => ({
            ...prevConfig,
            radio_parameters: [
                ...prevConfig.radio_parameters,
                { id: newFieldId, radio: "", radio_note: "", radio_initial: false },
            ],
        }));
    };
    const removeAdditionalRadioField = (id) => {
        setUpdatedObject((prevConfig) => ({
            ...prevConfig,
            radio_parameters: prevConfig.radio_parameters.filter((field) => field.id !== id),
        }));
    };
    const addAdditionalCheckboxField = (ev) => {
        ev.preventDefault();
        const newFieldId = updatedObject.checkbox_parameters.length + 1;
        setUpdatedObject((prevConfig) => ({
            ...prevConfig,
            checkbox_parameters: [
                ...prevConfig.checkbox_parameters,
                { id: newFieldId, checkbox: "", checkbox_note: "" },
            ],
        }));
    };
    const removeAdditionalCheckboxField = (id) => {
        setUpdatedObject((prevConfig) => ({
            ...prevConfig,
            checkbox_parameters: prevConfig.checkbox_parameters.filter((field) => field.id !== id),
        }));
    };

    const addAdditionalChoiceField = (ev) => {
        ev.preventDefault();
        const newFieldId = updatedObject.field_parameters.choices.length + 1;
        setUpdatedObject((prevConfig) => ({
            ...prevConfig,
            field_parameters: {
                ...prevConfig.field_parameters,
                choices: [...prevConfig.field_parameters.choices, { id: newFieldId, label: "", label_en: "" }],
            },
        }));
    };
    const removeAdditionalChoiceField = (id) => {
        setUpdatedObject((prevConfig) => ({
            ...prevConfig,
            field_parameters: {
                ...prevConfig.field_parameters,
                choices: prevConfig.field_parameters.choices.filter((field) => field.id !== id),
            },
        }));
    };
    const renderAdditionalButtonField = () => {
        return (
            <>
                <FormFieldGroup
                    label={
                        <span>
                            {t("Загружаемый файл")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                        </span>
                    }
                >
                    <DevForm.Select
                        name="sub_control_type"
                        choices={subControlTypeChoices}
                        onChange={(data) => {
                            const ev = { target: { ...data, name: "sub_control_type" } };
                            handleUpdateEventItem(ev);
                        }}
                        required={true}
                        value={updatedObject.sub_control_type || ""}
                        placeholder={subControlTypeLabels[updatedObject.sub_control_type] || ""}
                    />
                </FormFieldGroup>
                {updatedObject.sub_control_type === subControlType.image_button && (
                    <>
                        <FormFieldGroup
                            label={
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span style={{ marginRight: "5px" }}>{t("Тип загрузки изображения")}</span>
                                    <span style={{ color: "red" }}>*</span>
                                </div>
                            }
                        >
                            <DevForm.Select
                                value={updatedObject?.button_parameters?.check || ""}
                                name="check"
                                onChange={(data) => {
                                    const ev = { target: { ...data, name: "check" } };
                                    handleUpdateEventItem(ev);
                                }}
                                choices={checkTypeChoices}
                            ></DevForm.Select>
                            {errors?.check && (
                                <div className="error" style={{ color: "red" }}>
                                    {errors?.check}
                                </div>
                            )}
                        </FormFieldGroup>
                    </>
                )}
                {updatedObject.sub_control_type === subControlType.image_button &&
                    updatedObject?.button_parameters?.check === imageDownloadType.exact && (
                        <>
                            <FormFieldGroup
                                label={
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: "5px" }}>{t("Ограничение по высоте")}</span>
                                        <span style={{ color: "red" }}>*</span>
                                    </div>
                                }
                            >
                                <DevForm.TextInput
                                    type="number"
                                    name="x"
                                    value={updatedObject.button_parameters.x}
                                    onChange={handleUpdateEventItem}
                                    placeholder={t("Введите ограничение по высоте в пикселях")}
                                    required
                                />
                                {errors?.x && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors.x}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup
                                label={
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: "5px" }}>{t("Ограничение по ширине")}</span>
                                        <span style={{ color: "red" }}>*</span>
                                    </div>
                                }
                            >
                                <DevForm.TextInput
                                    type="number"
                                    name="y"
                                    value={updatedObject.button_parameters.y}
                                    onChange={handleUpdateEventItem}
                                    placeholder={t("Введите ограничение по ширине в пикселях")}
                                    required
                                />
                                {errors?.y && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors.y}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup label={t("Ограничение по объему")}>
                                <DevForm.TextInput
                                    type="number"
                                    name="max_volume"
                                    value={updatedObject.button_parameters.max_volume}
                                    onChange={handleUpdateEventItem}
                                    placeholder={t("Введите ограничение по объему в кб")}
                                    required
                                />
                                {errors?.max_volume && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors.max_volume}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup label={t("Ограничения для изображений")}>
                                <>
                                    {allowedImageTypes.map((image_type) => (
                                        <DevForm.Checkbox
                                            key={image_type}
                                            name="file_format"
                                            value={image_type}
                                            onChange={handleUpdateEventItem}
                                            type="checkbox"
                                            id={`checkbox_${image_type}`}
                                            label={image_type}
                                            checked={updatedObject.button_parameters?.file_format?.includes(image_type)}
                                        />
                                    ))}
                                    {errors?.file_format && (
                                        <div className="error" style={{ color: "red" }}>
                                            {errors?.file_format}
                                        </div>
                                    )}
                                </>
                            </FormFieldGroup>
                        </>
                    )}
                {updatedObject.sub_control_type === subControlType.image_button &&
                    updatedObject?.button_parameters?.check === imageDownloadType.between && (
                        <>
                            <FormFieldGroup
                                label={
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: "5px" }}>
                                            {t("Минимальное ограничение по высоте")}
                                        </span>
                                        <span style={{ color: "red" }}>*</span>
                                    </div>
                                }
                            >
                                <DevForm.TextInput
                                    type="number"
                                    name="x"
                                    value={updatedObject.button_parameters.x}
                                    onChange={handleUpdateEventItem}
                                    placeholder={t("Введите ограничение по высоте в пикселях")}
                                />
                                {errors?.x && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors.x}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup
                                label={
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: "5px" }}>
                                            {t("Минимальное ограничение по ширине")}
                                        </span>
                                        <span style={{ color: "red" }}>*</span>
                                    </div>
                                }
                            >
                                <DevForm.TextInput
                                    type="number"
                                    name="y"
                                    value={updatedObject.button_parameters.y}
                                    onChange={handleUpdateEventItem}
                                    placeholder={t("Введите ограничение по ширине в пикселях")}
                                />
                                {errors?.y && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors.y}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup
                                label={
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: "5px" }}>
                                            {t("Максимальное ограничение по высоте")}
                                        </span>
                                        <span style={{ color: "red" }}>*</span>
                                    </div>
                                }
                            >
                                <DevForm.TextInput
                                    type="number"
                                    name="x_2"
                                    value={updatedObject.button_parameters.x_2 || ""}
                                    onChange={handleUpdateEventItem}
                                    placeholder={t("Введите ограничение по высоте в пикселях")}
                                />
                                {errors?.x_2 && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors.x_2}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup
                                label={
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: "5px" }}>
                                            {t("Максимальное ограничение по высоте")}
                                        </span>
                                        <span style={{ color: "red" }}>*</span>
                                    </div>
                                }
                            >
                                <DevForm.TextInput
                                    type="number"
                                    name="y_2"
                                    value={updatedObject.button_parameters.y_2 || ""}
                                    onChange={handleUpdateEventItem}
                                    placeholder={t("Введите ограничение по ширине в пикселях")}
                                />
                                {errors?.y_2 && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors.y_2}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup label={t("Ограничение по объему")}>
                                <DevForm.TextInput
                                    type="number"
                                    name="max_volume"
                                    value={updatedObject.button_parameters.max_volume}
                                    onChange={handleUpdateEventItem}
                                    placeholder={t("Введите ограничение по объему в кб")}
                                />
                                {errors?.max_volume && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors.max_volume}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup label={t("Разрешенные типы")}>
                                <>
                                    {allowedImageTypes.map((image_type) => (
                                        <DevForm.Checkbox
                                            key={image_type}
                                            name="file_format"
                                            value={image_type}
                                            onChange={handleUpdateEventItem}
                                            type="checkbox"
                                            id={`checkbox_${image_type}`}
                                            label={image_type}
                                            checked={updatedObject.button_parameters?.file_format?.includes(image_type)}
                                        />
                                    ))}
                                    {errors?.file_format && (
                                        <div className="error" style={{ color: "red" }}>
                                            {errors?.file_format}
                                        </div>
                                    )}
                                </>
                            </FormFieldGroup>
                        </>
                    )}
                {updatedObject.sub_control_type === subControlType.video_button && (
                    <>
                        <FormFieldGroup
                            label={
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span style={{ marginRight: "5px" }}>{t("Ограничение по высоте")}</span>
                                    <span style={{ color: "red" }}>*</span>
                                </div>
                            }
                        >
                            <DevForm.TextInput
                                type="number"
                                name="x"
                                value={updatedObject?.button_parameters?.x || ""}
                                onChange={handleUpdateEventItem}
                                placeholder={t("Введите ограничение по высоте в пикселях")}
                                required
                            />
                            {errors?.x && (
                                <div className="error" style={{ color: "red" }}>
                                    {errors.x}
                                </div>
                            )}
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span style={{ marginRight: "5px" }}>{t("Ограничение по высоте")}</span>
                                    <span style={{ color: "red" }}>*</span>
                                </div>
                            }
                        >
                            <DevForm.TextInput
                                type="number"
                                name="y"
                                value={updatedObject?.button_parameters?.y || ""}
                                onChange={handleUpdateEventItem}
                                placeholder={t("Введите ограничение по ширине в пикселях")}
                                required
                            />
                            {errors?.y && (
                                <div className="error" style={{ color: "red" }}>
                                    {errors.y}
                                </div>
                            )}
                        </FormFieldGroup>
                        <FormFieldGroup label={t("Ограничение по объему")}>
                            <DevForm.TextInput
                                type="number"
                                name="max_volume"
                                value={updatedObject?.button_parameters?.max_volume || ""}
                                onChange={handleUpdateEventItem}
                                placeholder={t("Введите ограничение по объему в кб")}
                                required
                            />
                            {errors?.max_volume && (
                                <div className="error" style={{ color: "red" }}>
                                    {errors.max_volume}
                                </div>
                            )}
                        </FormFieldGroup>
                    </>
                )}
            </>
        );
    };

    const renderAdditionalRadioField = () => {
        return (
            <>
                {updatedObject.radio_parameters.map((field) => (
                    <React.Fragment key={field.id}>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Радиобаттон ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    data-id={field.id}
                                    name="radio"
                                    value={field.radio || ""}
                                    onChange={handleUpdateEventItem}
                                    required
                                    style={{ width: "400px", marginRight: "10px" }}
                                />
                                <Awesome
                                    type="trash-o"
                                    color="red"
                                    onClick={() => removeAdditionalRadioField(field.id)}
                                />
                                <DevForm.Checkbox
                                    type="radio"
                                    id={`defaultRadio_${field.id}`}
                                    name="radio_initial"
                                    label="по умолчанию"
                                    data-id={field.id}
                                    checked={field.radio_initial}
                                    onChange={handleUpdateEventItem}
                                    style={{ fontSize: "0.8em", marginLeft: "20px" }}
                                />
                            </div>
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Радиобаттон en ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    data-id={field.id}
                                    name="radio_en"
                                    value={field.radio_en || ""}
                                    onChange={handleUpdateEventItem}
                                    required
                                    style={{ width: "400px", marginRight: "10px" }}
                                />
                            </div>
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Примечание ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    data-id={field.id}
                                    name="radio_note"
                                    value={field.radio_note || ""}
                                    onChange={handleUpdateEventItem}
                                    required
                                    style={{ width: "400px", marginRight: "10px" }}
                                />
                            </div>
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Примечание en ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    data-id={field.id}
                                    name="radio_note_en"
                                    value={field.radio_note_en || ""}
                                    onChange={handleUpdateEventItem}
                                    required
                                    style={{ width: "400px", marginRight: "10px" }}
                                />
                            </div>
                            {errors?.radio_parameters &&
                                (field.radio_note === "" ||
                                    field.radio_note_en === "" ||
                                    field.radio === "" ||
                                    field.radio_en === "") && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors?.radio_parameters}
                                    </div>
                                )}
                        </FormFieldGroup>
                    </React.Fragment>
                ))}
                <div style={{ textAlign: "right", marginTop: "10px" }}>
                    <a href="#" onClick={addAdditionalRadioField}>
                        {t("Добавить радиобаттон")}
                    </a>
                </div>
            </>
        );
    };

    const renderAdditionalCheckBoxField = () => {
        return (
            <>
                {updatedObject.checkbox_parameters.map((field) => (
                    <React.Fragment key={field.id}>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Чекбокс ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    name="checkbox"
                                    value={field.checkbox || ""}
                                    data-id={field.id}
                                    onChange={handleUpdateEventItem}
                                    required
                                    style={{ marginRight: "10px" }}
                                />
                                <Awesome
                                    type="trash-o"
                                    color="red"
                                    onClick={() => removeAdditionalCheckboxField(field.id)}
                                />
                            </div>
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Чекбокс en ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    name="checkbox_en"
                                    value={field.checkbox_en || ""}
                                    data-id={field.id}
                                    onChange={handleUpdateEventItem}
                                    required
                                    style={{ marginRight: "20px" }}
                                />
                            </div>
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Примечание ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    name="checkbox_note"
                                    value={field.checkbox_note || ""}
                                    data-id={field.id}
                                    onChange={handleUpdateEventItem}
                                    required
                                    style={{ marginRight: "20px" }}
                                />
                            </div>
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Примечание en ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    name="checkbox_note_en"
                                    value={field.checkbox_note_en || ""}
                                    data-id={field.id}
                                    onChange={handleUpdateEventItem}
                                    required
                                    style={{ marginRight: "20px" }}
                                />
                            </div>
                            {errors?.checkbox_parameters &&
                                (field.checkbox_note === "" ||
                                    field.checkbox === "" ||
                                    field.checkbox_note_en === "" ||
                                    field.checkbox_en === "") && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors.checkbox_parameters}
                                    </div>
                                )}
                        </FormFieldGroup>
                    </React.Fragment>
                ))}
                <div style={{ textAlign: "right", marginTop: "10px" }}>
                    <a href="#" onClick={addAdditionalCheckboxField}>
                        {t("Добавить чекбокс")}
                    </a>
                </div>
            </>
        );
    };
    const renderAdditionalFieldField = () => {
        const controlTypeAndLabel = {
            text_field: t("Текстовое поле"),
            number_field: t("Числовое поле"),
            date_time_field: t("Даты и времени"),
            choice_field: t("Поле выбора"),
            url_field: t("Линк"),
            multi_choice_field: t("Множественный выбор"),
        };

        return (
            <>
                <FormFieldGroup
                    label={
                        <span>
                            {t("Тип поля")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                        </span>
                    }
                >
                    <DevForm.Select
                        name="sub_control_type"
                        choices={fieldTypeChoices}
                        onChange={(data) => {
                            const ev = { target: { ...data, name: "sub_control_type" } };
                            handleUpdateEventItem(ev);
                        }}
                        required={true}
                        value={updatedObject.sub_control_type || ""}
                        placeholder={controlTypeAndLabel[updatedObject.sub_control_type] || ""}
                    />
                </FormFieldGroup>

                {(updatedObject?.sub_control_type === subControlType.text_field ||
                    updatedObject.sub_control_type === subControlType.url_field) && (
                    <>
                        <FormFieldGroup label={t("Ограничение по длине символов")}>
                            <DevForm.TextInput
                                type="text"
                                name="max_length"
                                value={updatedObject.field_parameters?.max_length || ""}
                                onChange={(e) => handleUpdateEventItem(e)}
                                required
                            />
                            {errors?.field_parameters && (
                                <div className="error" style={{ color: "red" }}>
                                    {errors?.field_parameters}
                                </div>
                            )}
                        </FormFieldGroup>
                    </>
                )}
                {updatedObject?.sub_control_type === subControlType.number_field && (
                    <>
                        <FormFieldGroup label={t("Ограничение по максимальному значению")}>
                            <DevForm.TextInput
                                type="number"
                                name="max_num"
                                value={updatedObject?.field_parameters?.max_num || ""}
                                onChange={(e) => handleUpdateEventItem(e)}
                                required
                            />
                            {errors?.field_parameters && (
                                <div className="error" style={{ color: "red" }}>
                                    {errors?.field_parameters}
                                </div>
                            )}
                        </FormFieldGroup>
                    </>
                )}
                {(updatedObject?.sub_control_type === subControlType.choice_field ||
                    updatedObject.sub_control_type === subControlType.multi_choice_field) && (
                    <>
                        {updatedObject?.field_parameters?.choices?.map((field) => (
                            <React.Fragment key={field.id}>
                                <FormFieldGroup
                                    label={
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <span style={{ marginRight: "5px" }}>{t("Выбор для поля выбора")}</span>
                                            <span style={{ color: "red" }}>*</span>
                                        </div>
                                    }
                                >
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <DevForm.TextInput
                                            type="text"
                                            data-id={field.id}
                                            name="field_choice"
                                            value={field.label || ""}
                                            onChange={handleUpdateEventItem}
                                            required
                                            style={{ marginRight: "10px" }}
                                        />
                                        <Awesome
                                            type="trash-o"
                                            color="red"
                                            onClick={() => removeAdditionalChoiceField(field.id)}
                                        />
                                    </div>
                                </FormFieldGroup>
                                <FormFieldGroup
                                    label={
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <span style={{ marginRight: "5px" }}>{t("Выбор для поля выбора en")}</span>
                                            <span style={{ color: "red" }}>*</span>
                                        </div>
                                    }
                                >
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <DevForm.TextInput
                                            type="text"
                                            data-id={field.id}
                                            name="field_choice_en"
                                            value={field.label_en || ""}
                                            onChange={handleUpdateEventItem}
                                            required
                                            style={{ marginRight: "10px" }}
                                        />
                                    </div>
                                    {errors?.field_parameters && field.label === "" && (
                                        <div className="error" style={{ color: "red" }}>
                                            {errors?.field_parameters}
                                        </div>
                                    )}
                                </FormFieldGroup>
                            </React.Fragment>
                        ))}
                        <div style={{ textAlign: "right", marginTop: "10px" }}>
                            <a href="#" onClick={addAdditionalChoiceField}>
                                {t("Добавить поле выбора")}
                            </a>
                        </div>
                    </>
                )}
            </>
        );
    };

    const renderAdditionalFields = () => {
        if (updatedObject.control_type === controlType.button) {
            return renderAdditionalButtonField();
        }
        if (updatedObject.control_type === controlType.radio) {
            return renderAdditionalRadioField();
        }
        if (updatedObject.control_type === controlType.checkbox) {
            return renderAdditionalCheckBoxField();
        }
        if (updatedObject.control_type === controlType.field) {
            return renderAdditionalFieldField();
        }
        return null;
    };
    return (
        <DevModal
            size="lg"
            show={showEditModal}
            onHide={handleCloseEditModal}
            header={t("Редактор поля")}
            body={
                <>
                    <FormFieldGroup
                        label={
                            <span>
                                {t("Тип поля")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                            </span>
                        }
                    >
                        <DevForm.Select placeholder={control_type_labels[updatedObject.control_type]} disabled={true} />
                    </FormFieldGroup>
                    <FormFieldGroup
                        label={
                            <span>
                                {t("Название")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                            </span>
                        }
                    >
                        <DevForm.TextInput
                            name="label"
                            type="text"
                            value={updatedObject.label || ""}
                            placeholder="Введите значение"
                            onChange={handleUpdateEventItem}
                        />
                        {errors?.label && (
                            <div className="error" style={{ color: "red" }}>
                                {errors.label}
                            </div>
                        )}
                    </FormFieldGroup>
                    <FormFieldGroup
                        label={
                            <span>
                                {t("Название en")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                            </span>
                        }
                    >
                        <DevForm.TextInput
                            name="label_en"
                            type="text"
                            value={updatedObject.label_en || ""}
                            placeholder="Введите значение"
                            onChange={handleUpdateEventItem}
                        />
                        {errors?.label_en && (
                            <div className="error" style={{ color: "red" }}>
                                {errors.label_en}
                            </div>
                        )}
                    </FormFieldGroup>
                    <FormFieldGroup
                        label={
                            <span>
                                {t("Примечание")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                            </span>
                        }
                    >
                        <DevForm.TextInput
                            name="help_text"
                            type="text"
                            value={updatedObject.help_text || ""}
                            placeholder={t("Введите значение")}
                            onChange={handleUpdateEventItem}
                        />
                        {errors?.help_text && (
                            <div className="error" style={{ color: "red" }}>
                                {errors.help_text}
                            </div>
                        )}
                    </FormFieldGroup>
                    <FormFieldGroup
                        label={
                            <span>
                                {t("Примечание en")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                            </span>
                        }
                    >
                        <DevForm.TextInput
                            name="help_text_en"
                            type="text"
                            value={updatedObject.help_text_en || ""}
                            placeholder={t("Введите значение")}
                            onChange={handleUpdateEventItem}
                        />
                        {errors?.help_text_en && (
                            <div className="error" style={{ color: "red" }}>
                                {errors.help_text_en}
                            </div>
                        )}
                    </FormFieldGroup>
                    <FormFieldGroup
                        label={
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <span style={{ marginRight: "5px" }}>{t("Описание поля (хинт)")}</span>
                                <span style={{ color: "red" }}>*</span>
                            </div>
                        }
                    >
                        <DevForm.TextInput
                            name="tooltip"
                            type="text"
                            value={updatedObject.tooltip || ""}
                            placeholder={t("Введите значение")}
                            onChange={handleUpdateEventItem}
                        />
                        {errors?.tooltip && (
                            <div className="error" style={{ color: "red" }}>
                                {errors.tooltip}
                            </div>
                        )}
                    </FormFieldGroup>
                    <FormFieldGroup
                        label={
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <span style={{ marginRight: "5px" }}>{t("Описание поля (хинт) en")}</span>
                                <span style={{ color: "red" }}>*</span>
                            </div>
                        }
                    >
                        <DevForm.TextInput
                            name="tooltip_en"
                            type="text"
                            value={updatedObject.tooltip_en || ""}
                            placeholder={t("Введите значение")}
                            onChange={handleUpdateEventItem}
                        />
                        {errors?.tooltip_en && (
                            <div className="error" style={{ color: "red" }}>
                                {errors.tooltip_en}
                            </div>
                        )}
                    </FormFieldGroup>
                    {renderAdditionalFields()}
                    <FormFieldGroup label={t("Могут заполнять разработчики?")}>
                        <DevForm.Checkbox
                            id="is_developer_fill_allowed"
                            name="is_developer_fill_allowed"
                            checked={updatedObject.is_developer_fill_allowed}
                            onChange={handleUpdateEventItem}
                            type="switch"
                        />
                    </FormFieldGroup>
                </>
            }
            footer={
                <DevForm.Button variant="primary" name="save" onClick={submitUpdateEventItem}>
                    {t("Сохранить")}
                </DevForm.Button>
            }
            renderWrapper={(children) => <Form>{children}</Form>}
        />
    );
}

function CreateSchemeItemModal({ showModal, setShowModal, setApiData }) {
    const { api_post } = useRequest();

    const defaultFormValues = {
        control_type: controlType.field,
        label: "",
        label_en: "",
        help_text: "",
        help_text_en: "",
        tooltip: "",
        tooltip_en: "",
        sub_control_type: subControlType.text_field,
        button_parameters: {
            check: "",
            x: "",
            y: "",
            x_2: "",
            y_2: "",
            max_volume: "",
            file_format: [],
        },
        radio_parameters: [{ id: 1, radio: "", radio_en: "", radio_note: "", radio_note_en: "", radio_initial: false }],
        checkbox_parameters: [{ id: 1, checkbox: "", checkbox_en: "", checkbox_note: "", checkbox_note_en: "" }],
        field_parameters: { max_length: 0, max_num: 0, choices: [{ id: 1, label: "", label_en: "" }] },
        is_developer_fill_allowed: false,
    };
    const { t } = useTranslation();
    const [errors, setErrors] = useState({});
    const [configuration, setConfiguration] = useState(defaultFormValues);

    const controlTypeChoices = [
        { value: controlType.field, label: t("Поле") },
        { value: controlType.button, label: t("Кнопка") },
        { value: controlType.checkbox, label: t("Чекбокс") },
        { value: controlType.radio, label: t("Радиобаттон") },
    ];

    const fieldTypeChoices = [
        { value: subControlType.text_field, label: t("Текстовое поле") },
        { value: subControlType.number_field, label: t("Числовое поле") },
        { value: subControlType.date_time_field, label: t("Даты и времени") },
        { value: subControlType.choice_field, label: t("Поле выбора") },
        { value: subControlType.url_field, label: t("Линк") },
        { value: subControlType.multi_choice_field, label: t("Множественный выбор") },
    ];

    const imageDownloadTypeChoices = [
        { value: imageDownloadType.exact, label: t("Конкретный размер") },
        { value: imageDownloadType.between, label: t("В пределах") },
    ];

    const subControlTypeChoices = [
        { value: subControlType.image_button, label: t("Изображения") },
        { value: subControlType.video_button, label: t("Видео") },
    ];

    const handleModalClose = () => {
        setErrors({});
        setShowModal(false);
        setConfiguration(defaultFormValues);
    };
    const addAdditionalRadioField = (ev) => {
        ev.preventDefault();
        const newFieldId = configuration.radio_parameters.length + 1;
        setConfiguration((prevConfig) => ({
            ...prevConfig,
            radio_parameters: [
                ...prevConfig.radio_parameters,
                { id: newFieldId, radio: "", radio_en: "", radio_note: "", radio_note_en: "", radio_initial: false },
            ],
        }));
    };
    const removeAdditionalRadioField = (id) => {
        setConfiguration((prevConfig) => ({
            ...prevConfig,
            radio_parameters: prevConfig.radio_parameters.filter((field) => field.id !== id),
        }));
    };
    const addAdditionalChoiceField = (ev) => {
        ev.preventDefault();
        const newFieldId = configuration.field_parameters.choices.length + 1;
        setConfiguration((prevConfig) => ({
            ...prevConfig,
            field_parameters: {
                ...prevConfig.field_parameters,
                choices: [...prevConfig.field_parameters.choices, { id: newFieldId, label: "" }],
            },
        }));
    };

    const removeAdditionalChoiceField = (id) => {
        setConfiguration((prevConfig) => ({
            ...prevConfig,
            field_parameters: {
                ...prevConfig.field_parameters,
                choices: prevConfig.field_parameters.choices.filter((field) => field.id !== id),
            },
        }));
    };
    const addAdditionalCheckboxField = (ev) => {
        ev.preventDefault();
        const newFieldId = configuration.checkbox_parameters.length + 1;
        setConfiguration((prevConfig) => ({
            ...prevConfig,
            checkbox_parameters: [
                ...prevConfig.checkbox_parameters,
                { id: newFieldId, checkbox: "", checkbox_en: "", checkbox_note: "", checkbox_note_en: "" },
            ],
        }));
    };
    const removeAdditionalCheckboxField = (id) => {
        setConfiguration((prevConfig) => ({
            ...prevConfig,
            checkbox_parameters: prevConfig.checkbox_parameters.filter((field) => field.id !== id),
        }));
    };
    const handleConfiguration = (e) => {
        const { value, name } = e.target;
        setConfiguration((prevState) => {
            if (
                name === "check" ||
                name === "x" ||
                name === "y" ||
                name === "x_2" ||
                name === "y_2" ||
                name === "max_volume" ||
                name === "file_format"
            ) {
                if (name === "file_format") {
                    const currentFormat = configuration.button_parameters.file_format || [];
                    const updatedButtonParameters = e.target.checked
                        ? [...currentFormat, value]
                        : configuration.button_parameters.file_format.filter((item) => item !== value);

                    return {
                        ...prevState,
                        button_parameters: {
                            ...prevState.button_parameters,
                            [name]: updatedButtonParameters,
                        },
                    };
                }
                const updatedButtonParameters = { ...prevState.button_parameters, [name]: value };
                return {
                    ...prevState,
                    button_parameters: updatedButtonParameters,
                };
            }
            if (name === "is_developer_fill_allowed") {
                return {
                    ...prevState,
                    [name]: e.target.checked,
                };
            }
            if (
                name === "radio" ||
                name === "radio_note" ||
                name === "radio_initial" ||
                name === "radio_en" ||
                name === "radio_note_en"
            ) {
                if (name === "radio_initial") {
                    const updatedRadioParameters = prevState.radio_parameters.map((item) => ({
                        ...item,
                        radio_initial: item.id === parseInt(e.target.dataset.id),
                    }));
                    return {
                        ...prevState,
                        radio_parameters: updatedRadioParameters,
                    };
                }
                const updatedRadioButtons = prevState.radio_parameters.map((radioButtonField) => {
                    if (radioButtonField.id === parseInt(e.target.dataset.id)) {
                        return { ...radioButtonField, [name]: value };
                    }
                    return radioButtonField;
                });

                return {
                    ...prevState,
                    radio_parameters: updatedRadioButtons,
                };
            }
            if (
                name === "checkbox" ||
                name === "checkbox_note" ||
                name === "checkbox_en" ||
                name === "checkbox_note_en"
            ) {
                const updatedCheckboxButtons = prevState.checkbox_parameters.map((checkboxButtonField) => {
                    if (checkboxButtonField.id === parseInt(e.target.dataset.id)) {
                        return { ...checkboxButtonField, [name]: value };
                    }
                    return checkboxButtonField;
                });

                return {
                    ...prevState,
                    checkbox_parameters: updatedCheckboxButtons,
                };
            }
            if (name === "max_length" || name === "max_num" || name === "field_choice" || name === "field_choice_en") {
                if (name === "field_choice") {
                    const updatedFieldChoices = prevState.field_parameters.choices.map((fieldChoice) => {
                        if (fieldChoice.id === parseInt(e.target.dataset.id)) {
                            return { ...fieldChoice, label: value };
                        }
                        return fieldChoice;
                    });
                    return {
                        ...prevState,
                        field_parameters: {
                            ...prevState.field_parameters,
                            choices: updatedFieldChoices,
                        },
                    };
                }
                if (name === "field_choice_en") {
                    const updatedFieldChoices = prevState.field_parameters.choices.map((fieldChoice) => {
                        if (fieldChoice.id === parseInt(e.target.dataset.id)) {
                            return { ...fieldChoice, label_en: value };
                        }
                        return fieldChoice;
                    });
                    return {
                        ...prevState,
                        field_parameters: {
                            ...prevState.field_parameters,
                            choices: updatedFieldChoices,
                        },
                    };
                }
                const updatedFieldParameters = { ...prevState.field_parameters, [name]: value };
                return {
                    ...prevState,
                    field_parameters: updatedFieldParameters,
                };
            }
            return {
                ...prevState,
                [name]: value,
            };
        });
    };
    const submitConfiguration = (ev) => {
        const newConfiguration = {
            ...configuration,
            button_parameters: JSON.stringify(configuration.button_parameters),
            radio_parameters: JSON.stringify(configuration.radio_parameters),
            checkbox_parameters: JSON.stringify(configuration.checkbox_parameters),
            field_parameters: JSON.stringify(configuration.field_parameters),
        };
        api_post(ev, url_reverse("api:schema_item:list"), {
            params: { action: "add_event_item", ...newConfiguration },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                    setErrors({});
                    handleModalClose();
                } else if (resp.errors) {
                    setErrors(resp.errors);
                }
            },
        });
    };

    const renderAdditionalButtonField = () => {
        return (
            <>
                <FormFieldGroup
                    label={
                        <span>
                            {t("Загружаемый файл")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                        </span>
                    }
                >
                    <DevForm.Select
                        name="control_type"
                        choices={subControlTypeChoices}
                        onChange={(data) => {
                            const ev = { target: { ...data, name: "sub_control_type" } };
                            handleConfiguration(ev);
                        }}
                        required={true}
                        value={configuration.sub_control_type || ""}
                        placeholder={t("Вариант загрузки")}
                    />
                </FormFieldGroup>
                {configuration.sub_control_type === subControlType.image_button && (
                    <>
                        <FormFieldGroup
                            label={
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span style={{ marginRight: "5px" }}>{t("Тип загрузки изображения")}</span>
                                    <span style={{ color: "red" }}>*</span>
                                </div>
                            }
                        >
                            <DevForm.Select
                                name="check"
                                choices={imageDownloadTypeChoices}
                                onChange={(data) => {
                                    const ev = { target: { ...data, name: "check" } };
                                    handleConfiguration(ev);
                                }}
                                required={true}
                                value={configuration.button_parameters.check || ""}
                                placeholder={t("Тип загрузки изображения")}
                            />
                            {errors?.check && (
                                <div className="error" style={{ color: "red" }}>
                                    {errors?.check}
                                </div>
                            )}
                        </FormFieldGroup>
                    </>
                )}
                {configuration.sub_control_type === subControlType.image_button &&
                    configuration.button_parameters.check === imageDownloadType.exact && (
                        <>
                            <FormFieldGroup
                                label={
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: "5px" }}>{t("Ограничение по высоте")}</span>
                                        <span style={{ color: "red" }}>*</span>
                                    </div>
                                }
                            >
                                <DevForm.TextInput
                                    type="number"
                                    name="x"
                                    value={configuration.button_parameters.x || ""}
                                    onChange={handleConfiguration}
                                    placeholder={t("Введите ограничение по высоте в пикселях")}
                                    required
                                />
                                {errors?.x && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors?.x}
                                    </div>
                                )}
                            </FormFieldGroup>

                            <FormFieldGroup
                                label={
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: "5px" }}>{t("Ограничение по высоте")}</span>
                                        <span style={{ color: "red" }}>*</span>
                                    </div>
                                }
                            >
                                <DevForm.TextInput
                                    type="number"
                                    name="y"
                                    value={configuration.button_parameters.y || ""}
                                    onChange={handleConfiguration}
                                    placeholder={t("Введите ограничение по ширине в пикселях")}
                                    required
                                />
                                {errors?.y && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors?.y}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup label={t("Ограничение по объему")}>
                                <DevForm.TextInput
                                    type="number"
                                    name="max_volume"
                                    value={configuration.button_parameters.max_volume || ""}
                                    onChange={handleConfiguration}
                                    placeholder={t("Введите ограничение по объему в кб")}
                                    required
                                />
                                {errors?.max_volume && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors?.max_volume}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup label={t("Ограничения для изображений")}>
                                <>
                                    {allowedImageTypes.map((image_type) => (
                                        <DevForm.Checkbox
                                            key={image_type}
                                            name="file_format"
                                            value={image_type}
                                            onChange={handleConfiguration}
                                            type="checkbox"
                                            id={`checkbox_${image_type}`}
                                            label={image_type}
                                            checked={configuration.button_parameters.file_format.includes(image_type)}
                                        />
                                    ))}
                                    {errors?.file_format && (
                                        <div className="error" style={{ color: "red" }}>
                                            {errors?.file_format}
                                        </div>
                                    )}
                                </>
                            </FormFieldGroup>
                        </>
                    )}
                {configuration.sub_control_type === subControlType.image_button &&
                    configuration.button_parameters.check === imageDownloadType.between && (
                        <>
                            <FormFieldGroup
                                label={
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: "5px" }}>
                                            {t("Минимальное ограничение по высоте")}
                                        </span>
                                        <span style={{ color: "red" }}>*</span>
                                    </div>
                                }
                            >
                                <DevForm.TextInput
                                    type="number"
                                    name="x"
                                    value={configuration.button_parameters.x || ""}
                                    onChange={handleConfiguration}
                                    placeholder={t("Введите ограничение по высоте в пикселях")}
                                />
                                {errors?.x && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors?.x}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup
                                label={
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: "5px" }}>
                                            {t("Минимальное ограничение по высоте")}
                                        </span>
                                        <span style={{ color: "red" }}>*</span>
                                    </div>
                                }
                            >
                                <DevForm.TextInput
                                    type="number"
                                    name="y"
                                    value={configuration.button_parameters.y || ""}
                                    onChange={handleConfiguration}
                                    placeholder={t("Введите ограничение по ширине в пикселях")}
                                />
                                {errors?.y && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors?.y}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup
                                label={
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: "5px" }}>
                                            {t("Максимальное ограничение по высоте")}
                                        </span>
                                        <span style={{ color: "red" }}>*</span>
                                    </div>
                                }
                            >
                                <DevForm.TextInput
                                    type="number"
                                    name="x_2"
                                    value={configuration.button_parameters.x_2 || ""}
                                    onChange={handleConfiguration}
                                    placeholder={t("Введите ограничение по высоте в пикселях")}
                                />
                                {errors?.x_2 && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors?.x_2}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup
                                label={
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <span style={{ marginRight: "5px" }}>
                                            {t("Максимальное ограничение по высоте")}
                                        </span>
                                        <span style={{ color: "red" }}>*</span>
                                    </div>
                                }
                            >
                                <DevForm.TextInput
                                    type="number"
                                    name="y_2"
                                    value={configuration.button_parameters.y_2 || ""}
                                    onChange={handleConfiguration}
                                    placeholder={t("Введите ограничение по ширине в пикселях")}
                                />
                                {errors?.y_2 && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors?.y_2}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup label={t("Ограничение по объему")}>
                                <DevForm.TextInput
                                    type="number"
                                    name="max_volume"
                                    value={configuration.button_parameters.max_volume || ""}
                                    onChange={handleConfiguration}
                                    placeholder={t("Введите ограничение по объему в кб")}
                                />
                                {errors?.max_volume && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors?.max_volume}
                                    </div>
                                )}
                            </FormFieldGroup>
                            <FormFieldGroup label={t("Разрешенные типы")}>
                                <>
                                    {allowedImageTypes.map((image_type) => (
                                        <DevForm.Checkbox
                                            key={image_type}
                                            name="file_format"
                                            value={image_type}
                                            onChange={handleConfiguration}
                                            type="checkbox"
                                            id={`checkbox_${image_type}`}
                                            label={image_type}
                                            checked={configuration.button_parameters.file_format.includes(image_type)}
                                        />
                                    ))}
                                    {errors?.file_format && (
                                        <div className="error" style={{ color: "red" }}>
                                            {errors?.file_format}
                                        </div>
                                    )}
                                </>
                            </FormFieldGroup>
                        </>
                    )}
                {configuration.sub_control_type === subControlType.video_button && (
                    <>
                        <FormFieldGroup
                            label={
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span style={{ marginRight: "5px" }}>{t("Ограничение по высоте")}</span>
                                    <span style={{ color: "red" }}>*</span>
                                </div>
                            }
                        >
                            <DevForm.TextInput
                                type="number"
                                name="x"
                                value={configuration.button_parameters.x || ""}
                                onChange={handleConfiguration}
                                placeholder={t("Введите ограничение по высоте в пикселях")}
                                required
                            />
                            {errors?.x && (
                                <div className="error" style={{ color: "red" }}>
                                    {errors?.x}
                                </div>
                            )}
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <span style={{ marginRight: "5px" }}>{t("Ограничение по ширине")}</span>
                                    <span style={{ color: "red" }}>*</span>
                                </div>
                            }
                        >
                            <DevForm.TextInput
                                type="number"
                                name="y"
                                value={configuration.button_parameters.y || ""}
                                onChange={handleConfiguration}
                                placeholder={t("Введите ограничение по ширине в пикселях")}
                                required
                            />
                            {errors?.y && (
                                <div className="error" style={{ color: "red" }}>
                                    {errors?.y}
                                </div>
                            )}
                        </FormFieldGroup>
                        <FormFieldGroup label={t("Ограничение по объему")}>
                            <DevForm.TextInput
                                type="number"
                                name="max_volume"
                                value={configuration.button_parameters.max_volume || ""}
                                onChange={handleConfiguration}
                                placeholder={t("Введите ограничение по объему в кб")}
                                required
                            />
                            {errors?.max_volume && (
                                <div className="error" style={{ color: "red" }}>
                                    {errors?.max_volume}
                                </div>
                            )}
                        </FormFieldGroup>
                        <FormFieldGroup label={t("Ограничение по формату")}>
                            <>
                                {allowedVideoTypes.map((video_type) => (
                                    <DevForm.Checkbox
                                        key={video_type}
                                        name="file_format"
                                        value={video_type}
                                        onChange={handleConfiguration}
                                        type="checkbox"
                                        id={`checkbox_${video_type}`}
                                        label={video_type}
                                    />
                                ))}
                                {errors?.file_format && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors?.file_format}
                                    </div>
                                )}
                            </>
                        </FormFieldGroup>
                    </>
                )}
            </>
        );
    };
    const renderAdditionalRadioField = () => {
        return (
            <>
                {configuration.radio_parameters.map((field) => (
                    <React.Fragment key={field.id}>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Радиобаттон ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    data-id={field.id}
                                    name="radio"
                                    value={field.radio}
                                    onChange={handleConfiguration}
                                    required
                                    style={{ width: "400px", marginRight: "10px" }}
                                />
                                <Awesome
                                    type="trash-o"
                                    color="red"
                                    onClick={() => removeAdditionalRadioField(field.id)}
                                />
                                <DevForm.Checkbox
                                    type="radio"
                                    id={`defaultRadio_${field.id}`}
                                    name="radio_initial"
                                    label="по умолчанию"
                                    data-id={field.id}
                                    checked={field.radio_initial}
                                    onChange={handleConfiguration}
                                    style={{ fontSize: "0.8em", marginLeft: "20px" }}
                                />
                            </div>
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Радиобаттон en ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    data-id={field.id}
                                    name="radio_en"
                                    value={field.radio_en}
                                    onChange={handleConfiguration}
                                    required
                                    style={{ width: "400px", marginRight: "10px" }}
                                />
                            </div>
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Примечание ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    data-id={field.id}
                                    name="radio_note"
                                    value={field.radio_note}
                                    onChange={handleConfiguration}
                                    required
                                    style={{ width: "400px", marginRight: "10px" }}
                                />
                            </div>
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Примечание en ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    data-id={field.id}
                                    name="radio_note_en"
                                    value={field.radio_note_en}
                                    onChange={handleConfiguration}
                                    required
                                    style={{ width: "400px", marginRight: "10px" }}
                                />
                            </div>
                            {errors?.radio_parameters &&
                                (field.radio_note === "" ||
                                    field.radio === "" ||
                                    field.radio_note_en === "" ||
                                    field.radio_en === "") && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors?.radio_parameters}
                                    </div>
                                )}
                        </FormFieldGroup>
                    </React.Fragment>
                ))}
                <div style={{ textAlign: "right", marginTop: "10px" }}>
                    <a href="#" onClick={addAdditionalRadioField}>
                        {t("Добавить радиобаттон")}
                    </a>
                </div>
            </>
        );
    };

    const renderAdditionalCheckBoxField = () => {
        return (
            <>
                {configuration.checkbox_parameters.map((field) => (
                    <React.Fragment key={field.id}>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Чекбокс ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    name="checkbox"
                                    value={field.checkbox}
                                    data-id={field.id}
                                    onChange={handleConfiguration}
                                    required
                                    style={{ marginRight: "10px" }}
                                />
                                <Awesome
                                    type="trash-o"
                                    color="red"
                                    onClick={() => removeAdditionalCheckboxField(field.id)}
                                />
                            </div>
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Чекбокс en ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    name="checkbox_en"
                                    value={field.checkbox_en}
                                    data-id={field.id}
                                    onChange={handleConfiguration}
                                    required
                                    style={{ marginRight: "20px" }}
                                />
                            </div>
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Примечание ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    name="checkbox_note"
                                    value={field.checkbox_note}
                                    data-id={field.id}
                                    onChange={handleConfiguration}
                                    required
                                    style={{ marginRight: "20px" }}
                                />
                            </div>
                        </FormFieldGroup>
                        <FormFieldGroup
                            label={
                                <span>
                                    {t("Примечание en ") + field.id}{" "}
                                    <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                                </span>
                            }
                        >
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <DevForm.TextInput
                                    type="text"
                                    name="checkbox_note_en"
                                    value={field.checkbox_note_en}
                                    data-id={field.id}
                                    onChange={handleConfiguration}
                                    required
                                    style={{ marginRight: "20px" }}
                                />
                            </div>
                            {errors?.checkbox_parameters &&
                                (field.checkbox_note === "" ||
                                    field.checkbox === "" ||
                                    field.checkbox_note_en === "" ||
                                    field.checkbox_en === "") && (
                                    <div className="error" style={{ color: "red" }}>
                                        {errors?.checkbox_parameters}
                                    </div>
                                )}
                        </FormFieldGroup>
                    </React.Fragment>
                ))}
                <div style={{ textAlign: "right", marginTop: "10px" }}>
                    <a href="#" onClick={addAdditionalCheckboxField}>
                        {t("Добавить чекбокс")}
                    </a>
                </div>
            </>
        );
    };

    const renderAdditionalFieldField = () => {
        return (
            <>
                <FormFieldGroup
                    label={
                        <span>
                            {t("Тип поля")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                        </span>
                    }
                >
                    <DevForm.Select
                        name="sub_control_type"
                        choices={fieldTypeChoices}
                        onChange={(data) => {
                            const ev = { target: { ...data, name: "sub_control_type" } };
                            handleConfiguration(ev);
                        }}
                        required={true}
                        value={configuration.sub_control_type || ""}
                        placeholder={t("Выберите тип поля")}
                    />
                </FormFieldGroup>

                {(configuration.sub_control_type === subControlType.text_field ||
                    configuration.sub_control_type === subControlType.url_field) && (
                    <>
                        <FormFieldGroup label={t("Ограничение по длине символов")}>
                            <DevForm.TextInput
                                type="text"
                                name="max_length"
                                value={configuration.field_parameters.max_length || ""}
                                onChange={(e) => handleConfiguration(e)}
                                required
                            />
                            {errors?.field_parameters && (
                                <div className="error" style={{ color: "red" }}>
                                    {errors?.field_parameters}
                                </div>
                            )}
                        </FormFieldGroup>
                    </>
                )}
                {configuration.sub_control_type === subControlType.number_field && (
                    <>
                        <FormFieldGroup label={t("Ограничение по максимальному значению")}>
                            <DevForm.TextInput
                                type="number"
                                name="max_num"
                                value={configuration.field_parameters.max_num}
                                onChange={(e) => handleConfiguration(e)}
                                required
                            />
                            {errors?.field_parameters && (
                                <div className="error" style={{ color: "red" }}>
                                    {errors?.field_parameters}
                                </div>
                            )}
                        </FormFieldGroup>
                    </>
                )}
                {(configuration.sub_control_type === subControlType.choice_field ||
                    configuration.sub_control_type === subControlType.multi_choice_field) && (
                    <>
                        {configuration.field_parameters.choices.map((field) => (
                            <React.Fragment key={field.id}>
                                <FormFieldGroup
                                    label={
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <span style={{ marginRight: "5px" }}>{t("Выбор для поля выбора")}</span>
                                            <span style={{ color: "red" }}>*</span>
                                        </div>
                                    }
                                >
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <DevForm.TextInput
                                            type="text"
                                            data-id={field.id}
                                            name="field_choice"
                                            value={field.label || ""}
                                            onChange={handleConfiguration}
                                            required
                                            style={{ marginRight: "10px" }}
                                        />
                                        <Awesome
                                            type="trash-o"
                                            color="red"
                                            onClick={() => removeAdditionalChoiceField(field.id)}
                                        />
                                    </div>
                                </FormFieldGroup>
                                <FormFieldGroup
                                    label={
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <span style={{ marginRight: "5px" }}>{t("Выбор для поля выбора en")}</span>
                                            <span style={{ color: "red" }}>*</span>
                                        </div>
                                    }
                                >
                                    <div style={{ display: "flex", alignItems: "center" }}>
                                        <DevForm.TextInput
                                            type="text"
                                            data-id={field.id}
                                            name="field_choice_en"
                                            value={field.label_en || ""}
                                            onChange={handleConfiguration}
                                            required
                                            style={{ marginRight: "20px" }}
                                        />
                                    </div>
                                    {errors?.field_parameters && (field.label === "" || field.label_en === "") && (
                                        <div className="error" style={{ color: "red" }}>
                                            {errors?.field_parameters}
                                        </div>
                                    )}
                                </FormFieldGroup>
                            </React.Fragment>
                        ))}
                        <div style={{ textAlign: "right", marginTop: "10px" }}>
                            <a href="#" onClick={addAdditionalChoiceField}>
                                {t("Добавить поле выбора")}
                            </a>
                        </div>
                    </>
                )}
            </>
        );
    };

    const renderAdditionalFields = () => {
        if (configuration.control_type === controlType.button) {
            return renderAdditionalButtonField();
        }
        if (configuration.control_type === controlType.radio) {
            return renderAdditionalRadioField();
        }
        if (configuration.control_type === controlType.checkbox) {
            return renderAdditionalCheckBoxField();
        }
        if (configuration.control_type === controlType.field) {
            return renderAdditionalFieldField();
        }
        return null;
    };

    const handleFieldTypeChange = (date) => {
        if (date.value === controlType.button) {
            const newConfiguration = defaultFormValues;
            newConfiguration.sub_control_type = subControlType.image_button;
            newConfiguration.button_parameters.check = imageDownloadType.exact;
            setConfiguration(newConfiguration);
        } else {
            setConfiguration(defaultFormValues);
        }
        const e = { target: { ...date, name: "control_type" } };
        handleConfiguration(e);
        setErrors({});
    };

    return (
        <DevModal
            size="lg"
            show={showModal}
            onHide={handleModalClose}
            header={t("Конструктор поля")}
            body={
                <>
                    <FormFieldGroup
                        label={
                            <span>
                                {t("Тип поля")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                            </span>
                        }
                    >
                        <DevForm.Select
                            name="control_type"
                            choices={controlTypeChoices}
                            onChange={(data) => handleFieldTypeChange(data)}
                            required={true}
                            value={configuration.control_type}
                        />
                    </FormFieldGroup>
                    <FormFieldGroup
                        label={
                            <span>
                                {t("Название")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                            </span>
                        }
                    >
                        <DevForm.TextInput
                            name="label"
                            type="text"
                            value={configuration.label}
                            placeholder="Введите значение"
                            onChange={handleConfiguration}
                        />
                        {errors.label && (
                            <div className="error" style={{ color: "red" }}>
                                {errors.label}
                            </div>
                        )}
                    </FormFieldGroup>
                    <FormFieldGroup
                        label={
                            <span>
                                {t("Название en")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                            </span>
                        }
                    >
                        <DevForm.TextInput
                            name="label_en"
                            type="text"
                            value={configuration.label_en}
                            placeholder="Введите значение"
                            onChange={handleConfiguration}
                        />
                        {errors.label_en && (
                            <div className="error" style={{ color: "red" }}>
                                {errors.label_en}
                            </div>
                        )}
                    </FormFieldGroup>
                    <FormFieldGroup
                        label={
                            <span>
                                {t("Примечание")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                            </span>
                        }
                    >
                        <DevForm.TextInput
                            name="help_text"
                            type="text"
                            value={configuration.help_text}
                            placeholder="Введите значение"
                            onChange={handleConfiguration}
                        />
                        {errors?.help_text && (
                            <div className="error" style={{ color: "red" }}>
                                {errors?.help_text}
                            </div>
                        )}
                    </FormFieldGroup>
                    <FormFieldGroup
                        label={
                            <span>
                                {t("Примечание en")} <span style={{ color: "red", marginLeft: "5px" }}>*</span>
                            </span>
                        }
                    >
                        <DevForm.TextInput
                            name="help_text_en"
                            type="text"
                            value={configuration.help_text_en}
                            placeholder="Введите значение"
                            onChange={handleConfiguration}
                        />
                        {errors?.help_text_en && (
                            <div className="error" style={{ color: "red" }}>
                                {errors?.help_text_en}
                            </div>
                        )}
                    </FormFieldGroup>
                    <FormFieldGroup
                        label={
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <span style={{ marginRight: "5px" }}>{t("Описание поля (хинт)")}</span>
                                <span style={{ color: "red" }}>*</span>
                            </div>
                        }
                    >
                        <DevForm.TextInput
                            name="tooltip"
                            type="text"
                            value={configuration.tooltip}
                            placeholder="Введите значение"
                            onChange={handleConfiguration}
                        />
                        {errors?.tooltip && (
                            <div className="error" style={{ color: "red" }}>
                                {errors?.tooltip}
                            </div>
                        )}
                    </FormFieldGroup>
                    <FormFieldGroup
                        label={
                            <div style={{ display: "flex", alignItems: "center" }}>
                                <span style={{ marginRight: "5px" }}>{t("Описание поля (хинт) en")}</span>
                                <span style={{ color: "red" }}>*</span>
                            </div>
                        }
                    >
                        <DevForm.TextInput
                            name="tooltip_en"
                            type="text"
                            value={configuration.tooltip_en}
                            placeholder="Введите значение"
                            onChange={handleConfiguration}
                        />
                        {errors?.tooltip_en && (
                            <div className="error" style={{ color: "red" }}>
                                {errors?.tooltip_en}
                            </div>
                        )}
                    </FormFieldGroup>
                    {renderAdditionalFields()}
                    <FormFieldGroup label={t("Могут заполнять разработчики?")}>
                        <DevForm.Checkbox
                            id={"is_developer_fill_allowed"}
                            name={"is_developer_fill_allowed"}
                            checked={configuration.is_developer_fill_allowed}
                            onChange={handleConfiguration}
                            type="switch"
                        />
                    </FormFieldGroup>
                </>
            }
            footer={
                <DevForm.Button variant="primary" name="save" onClick={submitConfiguration}>
                    {t("Сохранить")}
                </DevForm.Button>
            }
            renderWrapper={(children) => <Form>{children}</Form>}
        />
    );
}

function SchemeItemListSortable({ apiData, setApiData, handleOpenEditModal }) {
    const { t } = useTranslation();
    const { api_post } = useRequest();

    const handleDeleteEventItem = (id) => {
        api_post(null, url_reverse("api:schema_item:list"), {
            params: { action: "delete_event_item", event_item_id: id },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                }
            },
        });
    };

    const handleCloneEventItem = (id) => {
        api_post(null, url_reverse("api:schema_item:list"), {
            params: { action: "clone_event_item", event_item_id: id },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                }
            },
        });
    };

    return (
        <DndTable
            apiData={apiData}
            setApiData={setApiData}
            apiUrl={url_reverse("api:schema_item:list")}
            tds={(el) => (
                <>
                    <td>
                        <b>{el.label}</b>
                    </td>
                    <td style={{ width: "15%" }}>
                        {el.control_type === controlType.radio && (
                            <>
                                {el.radio_parameters.map((obj) => (
                                    <div
                                        key={`radio_parameters.${obj.id}`}
                                        style={{ display: "flex", alignItems: "center" }}
                                    >
                                        <DevForm.Checkbox
                                            type="radio"
                                            label={
                                                <span>
                                                    {obj.radio} <small className="text-muted">({obj.radio_note})</small>
                                                </span>
                                            }
                                            disabled={true}
                                        />
                                    </div>
                                ))}
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <FormFieldHelpText help_text={el.help_text} />
                                    <div style={{ marginLeft: "10px" }}>
                                        <Hint tooltip={el.tooltip} />
                                    </div>
                                </div>
                            </>
                        )}
                        {el.control_type === controlType.checkbox && (
                            <>
                                {el.checkbox_parameters.map((obj) => (
                                    <div key={obj.id} style={{ display: "flex", alignItems: "center" }}>
                                        <Form.Check
                                            type="checkbox"
                                            label={
                                                <span>
                                                    {obj.checkbox}{" "}
                                                    <small className="text-muted">({obj.checkbox_note})</small>
                                                </span>
                                            }
                                            disabled={true}
                                        />
                                    </div>
                                ))}
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <FormFieldHelpText help_text={el.help_text} />
                                    <div style={{ marginLeft: "10px" }}>
                                        <Hint tooltip={el.tooltip} />
                                    </div>
                                </div>
                            </>
                        )}
                        {el.control_type === controlType.field && (
                            <>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    {el.sub_control_type === subControlType.choice_field && (
                                        <>
                                            <DevForm.Select
                                                disabled={true}
                                                defaultValue={el.field_parameters.choices[0].label}
                                            />
                                        </>
                                    )}
                                    {el.sub_control_type === subControlType.multi_choice_field && (
                                        <DevForm.SelectMultiple choices={el.field_parameters.choices} disabled={true} />
                                    )}
                                    {el.sub_control_type === subControlType.number_field && (
                                        <DevForm.TextInput type="number" disabled={true} />
                                    )}
                                    {el.sub_control_type === subControlType.date_time_field && (
                                        <Form.Control type="date" disabled={true} />
                                    )}
                                    {el.sub_control_type === subControlType.text_field && (
                                        <DevForm.TextInput type="text" disabled={true} />
                                    )}
                                    {el.sub_control_type === subControlType.url_field && (
                                        <DevForm.TextInput type="text" disabled={true} />
                                    )}
                                    <div style={{ marginLeft: "10px" }}>
                                        <span style={{ float: "right" }}>
                                            <Hint tooltip={el.tooltip} />
                                        </span>
                                    </div>
                                </div>
                                <FormFieldHelpText help_text={el.help_text} />
                            </>
                        )}
                        {el.control_type === controlType.button && (
                            <>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                    <Form.Control type="file" disabled={true} style={{ width: "250px" }} />
                                    <div style={{ marginLeft: "10px" }}>
                                        <span style={{ float: "right" }}>
                                            <Hint tooltip={el.tooltip} />
                                        </span>
                                    </div>
                                </div>
                                <FormFieldHelpText help_text={el.help_text} />
                            </>
                        )}
                    </td>
                    <td style={{ width: "25%" }}>
                        {el.is_developer_fill_allowed && (
                            <DevForm.Checkbox disabled={true} inline label={t("Запросить материал у разработчика")} />
                        )}
                    </td>
                    <td style={{ width: "130px", textAlign: "right" }}>
                        <Button size="sm" variant="outline-primary" onClick={() => handleCloneEventItem(el.id)}>
                            <Awesome type="clone" />
                        </Button>{" "}
                        <Button size="sm" variant="outline-primary" onClick={() => handleOpenEditModal(el)}>
                            <Awesome type="pencil" />
                        </Button>{" "}
                        <Button size="sm" variant="outline-danger" onClick={() => handleDeleteEventItem(el.id)}>
                            <Awesome type="trash-o" />
                        </Button>{" "}
                    </td>
                </>
            )}
        />
    );
}
